import React, { ReactElement, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Colors } from '../constants/colors';
import { Device } from '../constants/device';
import { USE_CASE_PATHS, UseCase, useCases } from '../config/useCasesConfig';
import { localizedPath } from '../helpers/localizedPath';
import { useUseCase } from '../helpers/useUseCase';
import { USE_CASE_PICKER_ID } from './UseCaseTabs';

/**
 * Keeps the use case in sight and switchable anywhere on the home page: a bar
 * pinned to the top of the screen once the hero picker has scrolled away.
 * Phones get a select instead of the four options, which would not fit a row.
 * Hidden until the observer reports, so the prerendered page matches hydration.
 */
export const UseCaseBar = (): ReactElement => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const selected = useUseCase();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const picker = document.getElementById(USE_CASE_PICKER_ID);
    if (!picker || !('IntersectionObserver' in window)) {
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0));
    observer.observe(picker);
    return () => observer.disconnect();
  }, []);

  const pathOf = (useCase: UseCase) => localizedPath(pathname, USE_CASE_PATHS[useCase]);
  // A pick from the bar takes the visitor back up to the overview of that use case.
  const showOverview = () => document.getElementById(USE_CASE_PICKER_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <Bar aria-label={t('section.useCases')} aria-hidden={!visible} $visible={visible}>
      <Label htmlFor="use-case-bar-select">{t('section.useCases')}:</Label>
      <Options>
        {useCases.map((useCase) => (
          <li key={useCase}>
            <Option to={pathOf(useCase)} onClick={showOverview} tabIndex={visible ? undefined : -1} aria-current={useCase === selected ? 'page' : undefined} $selected={useCase === selected}>
              {t(`useCases.items.${useCase}.title`)}
            </Option>
          </li>
        ))}
      </Options>
      <Select
        id="use-case-bar-select"
        tabIndex={visible ? undefined : -1}
        value={selected}
        onChange={(event) => {
          navigate(pathOf(event.target.value as UseCase));
          showOverview();
        }}
      >
        {useCases.map((useCase) => (
          <option key={useCase} value={useCase}>
            {t(`useCases.items.${useCase}.title`)}
          </option>
        ))}
      </Select>
    </Bar>
  );
};

const Bar = styled.nav<{ $visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 10px 16px;
  background: ${Colors.white};
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
  transform: translateY(${({ $visible }) => ($visible ? '0' : '-100%')});
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  transition:
    transform 0.25s ease,
    visibility 0.25s;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${Colors.primaryDark};

  /* Phones need the whole row for the select; the label stays for screen readers. */
  ${Device.mobile} {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
  }
`;

const Options = styled.ul`
  display: flex;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;

  ${Device.laptop} {
    display: none;
  }
`;

const Option = styled(Link)<{ $selected: boolean }>`
  display: inline-block;
  padding: 4px 14px;
  border-radius: 20px;
  border: 2px solid ${Colors.primary};
  background: ${({ $selected }) => ($selected ? Colors.primary : 'transparent')};
  color: ${({ $selected }) => ($selected ? Colors.white : Colors.primary)};
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  white-space: nowrap;
  text-decoration: none;

  &:hover {
    background: ${({ $selected }) => ($selected ? Colors.primary : Colors.primaryLight)};
  }
`;

const Select = styled.select`
  display: none;
  min-width: 0;
  max-width: 100%;
  padding: 6px 10px;
  border-radius: 20px;
  border: 2px solid ${Colors.primary};
  background: ${Colors.white};
  color: ${Colors.primary};
  font: inherit;
  font-size: 14px;
  font-weight: 700;

  ${Device.laptop} {
    display: block;
  }

  ${Device.mobile} {
    width: 100%;
  }
`;
